import {
  Box,
  Button,
  Flex,
  HStack,
  MenuItem,
  MenuList,
  Spinner,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import React, { useContext, useEffect, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { Link, useHistory, useLocation } from 'react-router-dom';
import download from 'downloadjs';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import {
  Heading,
  Input,
  Menu,
  Pagination,
  StatusBadge,
  Table,
} from '~/components';
import { PAGE_SIZE, ROUTER_PATHS } from '~/constants';
import { useAuth, useQueryParams, useRoleCan } from '~/hooks';
import { useDebounce } from '~/hooks/useDebounce';
import { useOccurrences } from '~/hooks/useOccurrences';
import { replaceParameters } from '~/utils/replaceParamaters';
import { FilterContext } from '~/contexts/FilterContext/FilterContext';
import { updateUrlParams } from '~/utils/updateUrlParams';
import { getOccurrencesXlsx } from '~/services';
import { useFeatureFlags } from '~/contexts/FeatureFlagsContext/FeatureFlagsContext';

export const Occurrences: React.VFC = () => {
  const history = useHistory();
  const location = useLocation();
  const queryParams = useQueryParams();

  const [searchTmp, setSearchTmp] = useState(() => {
    return queryParams.get('busca') ?? '';
  });
  const [parametros, setParametros] = useState(() => {
    return {
      busca: queryParams.get('busca') ?? '',
      pagina: queryParams.get('pagina') ?? '1',
      paginacao: queryParams.get('paginacao') ?? PAGE_SIZE.toString(),
      order: queryParams.get('order') ?? '',
      orderBy: queryParams.get('orderBy') ?? '',
    };
  });

  const { user } = useAuth();
  const { groups, headOffices, branches } = useContext(FilterContext);
  const [isLoadingDownload, setIsLoadingDownload] = useState(false);
  const toast = useToast();

  const { data, isLoading, isFetching, error, refetch } = useOccurrences(
    Number(parametros.pagina),
    parametros.orderBy,
    parametros.order,
    parametros.busca,
    { groups, headOffices, branches },
    Number(parametros.paginacao),
  );

  useDebounce(
    () => {
      setParametros({ ...parametros, busca: searchTmp, pagina: '1' });
      updateUrlParams(
        { ...parametros, busca: searchTmp, pagina: '1' },
        history,
      );
      refetch();
    },
    1000,
    [searchTmp],
  );

  useEffect(() => {
    refetch();
  }, []);

  useEffect(() => {
    setParametros({ ...parametros, pagina: '1' });
    updateUrlParams({ ...parametros, pagina: '1' }, history);
    refetch();
  }, [groups, headOffices, branches]);

  useEffect(() => {
    const pagina = queryParams.get('pagina');
    setParametros({
      ...parametros,
      pagina: pagina ?? '1',
      order: queryParams.get('order') ?? sortDirection,
      orderBy: queryParams.get('orderBy') ?? sortField,
    });
    updateUrlParams(
      {
        ...parametros,
        pagina: pagina ?? '1',
        order: queryParams.get('order') ?? sortDirection,
        orderBy: queryParams.get('orderBy') ?? sortField,
      },
      history,
    );
  }, [location.search]);
  const features = useFeatureFlags();

  const isClient = useRoleCan({ roles: ['cliente'] });
  const featureEnabled = features.aberturaChamadosEnabled;

  const [sortField, setSortField] = useState(
    queryParams.get('orderBy') ?? 'dt_aberturachamado',
  );
  const [sortDirection, setSortDirection] = useState(
    queryParams.get('order') ?? 'DESC',
  );

  function handleChamadoOrder(field: string) {
    setSortField(field);
    setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
  }

  useEffect(() => {
    setParametros({
      ...parametros,
      order: sortDirection,
      orderBy: sortField,
      pagina: queryParams.get('pagina') ?? '1',
    });
    updateUrlParams(
      {
        ...parametros,
        order: sortDirection,
        orderBy: sortField,
        pagina: queryParams.get('pagina') ?? '1',
      },
      history,
    );
  }, [sortField, sortDirection]);

  const getDirection = (key: string) => {
    return sortField === key ? sortDirection : undefined;
  };

  const handlePageSizeChange = (e: any) => {
    setParametros({
      ...parametros,
      paginacao: e.target.value,
      pagina: '1',
      order: sortDirection,
      orderBy: sortField,
    });
    updateUrlParams(
      {
        ...parametros,
        paginacao: e.target.value,
        pagina: '1',
        order: sortDirection,
        orderBy: sortField,
      },
      history,
    );
  };

  const handleDownload = async () => {
    setIsLoadingDownload(true);
    getOccurrencesXlsx(parametros.busca, parametros.orderBy, parametros.order, {
      groups,
      headOffices,
      branches,
    })
      .then(r => {
        if (r.data.size > 0) {
          download(
            new Blob([r.data], { type: r.data.type }),
            r.headers['content-disposition'].match(/filename="(.+)"/)[1],
          );
        } else {
          toast({
            title: r?.data?.error,
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(async err => {
        // recuperando o erro do back
        // https://github.com/axios/axios/issues/3779
        const error2 = JSON.parse(await err.response.data.text());

        toast({
          title: `Erro`,
          description: error2.detail,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => setIsLoadingDownload(false));
  };

  return (
    <Box px="8" py="4">
      <Heading>Chamados</Heading>
      <Flex pt={2} flexDir="column" w="100%">
        <Flex justify="space-between" py={5}>
          <Flex align="center" gridGap={4}>
            <Input
              name="search"
              type="text"
              icon={FiSearch}
              value={searchTmp}
              onChange={e => setSearchTmp(e.target.value)}
              placeholder="Procure por chamado, nº série ou produto"
              w={350}
              fontSize="15px"
            />
            {isFetching && <Spinner />}
          </Flex>
          <HStack
            gridGap={2}
            justifyContent="space-between"
            alignItems="baseline"
          >
            {isClient && featureEnabled && (
              <Button
                color="white"
                background="gray.900"
                _hover={{
                  bgColor: 'black',
                }}
                onClick={() => history.push(ROUTER_PATHS.PORTAL.NEW_OCCURRENCE)}
              >
                Abrir chamado
              </Button>
            )}
            <Button
              disabled={isLoadingDownload || isLoading}
              onClick={handleDownload}
              color="white"
              background="gray.900"
              _hover={{
                bgColor: 'black',
              }}
              style={{ marginTop: '20px' }}
            >
              Exportar
              {isLoadingDownload && <Spinner style={{ marginLeft: '10px' }} />}
            </Button>
          </HStack>
        </Flex>
        {isLoading ? (
          <Flex justify="center">
            <Spinner />
          </Flex>
        ) : error ? (
          <Flex justify="center">
            <Text>Falha ao obter dados dos chamados.</Text>
          </Flex>
        ) : (
          <>
            <Table>
              <Thead>
                <Tr>
                  <Th>N° Chamado</Th>
                  <Th>N° Série</Th>
                  <Th>Código SKU</Th>
                  <Th>Modelo</Th>
                  <Th>
                    <Flex
                      gap={2}
                      cursor="pointer"
                      onClick={() => handleChamadoOrder('dt_aberturachamado')}
                    >
                      Data Abertura{' '}
                      {getDirection('dt_aberturachamado') === 'ASC' ? (
                        <ChevronDownIcon fontSize={14} ml="2" />
                      ) : (
                        <ChevronUpIcon fontSize={14} ml="2" />
                      )}
                    </Flex>
                  </Th>
                  <Th>
                    <Flex
                      gap={2}
                      cursor="pointer"
                      onClick={() =>
                        handleChamadoOrder('dt_encerramentochamado')
                      }
                    >
                      Data Encerramento{' '}
                      {getDirection('dt_encerramentochamado') === 'ASC' ? (
                        <ChevronDownIcon fontSize={14} ml="2" />
                      ) : (
                        <ChevronUpIcon fontSize={14} ml="2" />
                      )}
                    </Flex>
                  </Th>
                  <Th>Código Cliente</Th>
                  <Th>Razão social / CNPJ</Th>
                  <Th>Grupo econômico</Th>
                  <Th>Status</Th>
                  <Th style={{ display: 'none' }} />
                </Tr>
              </Thead>
              <Tbody>
                {data &&
                  data.results.map(occurrence => (
                    <Tr key={`${occurrence.id_chamado}`}>
                      <Td>
                        {user && user.type === 'Interno' ? (
                          <Link
                            to={replaceParameters(
                              ROUTER_PATHS.PORTAL.OCCURRENCE_DETAIL,
                              String(occurrence.id_chamado),
                            )}
                          >
                            <Text
                              _hover={{
                                color: 'gray.900',
                                textDecoration: 'underline',
                              }}
                            >
                              {occurrence.nu_chamado}
                            </Text>
                          </Link>
                        ) : (
                          <Text
                            _hover={{
                              color: 'gray.900',
                            }}
                          >
                            {occurrence.nu_chamado}
                          </Text>
                        )}
                      </Td>
                      <Td>{occurrence.nu_serie}</Td>
                      <Td>{occurrence.sku || '-'}</Td>
                      <Td>{occurrence.modelo || '-'}</Td>
                      <Td>{occurrence?.formattedOpeningDate || '-'}</Td>
                      <Td>{occurrence?.formattedClosingDate || '-'}</Td>
                      <Td>{occurrence.cod_cliente || '-'}</Td>
                      <Td>
                        <Text>{occurrence.nm_empresa || '-'}</Text>
                        <Text>{occurrence.cnpj_empresa || '-'}</Text>
                      </Td>
                      <Td>{occurrence.nm_grupoeconomico || '-'}</Td>
                      <Td>
                        <StatusBadge variant="">
                          {occurrence.formattedStatus}
                        </StatusBadge>
                      </Td>
                      <Td style={{ display: 'none' }}>
                        <Menu>
                          <MenuList>
                            <Link
                              to={replaceParameters(
                                ROUTER_PATHS.PORTAL.OCCURRENCE_DETAIL,
                                String(occurrence.id_chamado),
                              )}
                            >
                              <MenuItem>Visualizar detalhes</MenuItem>
                            </Link>
                          </MenuList>
                        </Menu>
                      </Td>
                    </Tr>
                  ))}
              </Tbody>
            </Table>
            <Flex justify="flex-end">
              {data && (
                <Pagination
                  totalCountOfRegisters={data.count}
                  currentPage={Number(parametros.pagina)}
                  onPageChange={_page => {
                    setParametros({ ...parametros, pagina: _page.toString() });
                    updateUrlParams(
                      { ...parametros, pagina: _page.toString() },
                      history,
                    );
                  }}
                  onPageSizeChange={handlePageSizeChange}
                  registersPerPage={Number(parametros.paginacao) ?? PAGE_SIZE}
                />
              )}
            </Flex>
          </>
        )}
      </Flex>
    </Box>
  );
};
